import { createContext, useContext, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Calendar, ScanLine, Users, Award, Megaphone, Smartphone, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { Wordmark, LogoLockup } from "@/components/Logo";
import { useAuth } from "@/lib/auth";

/** Bilingual copy for the whole marketing page, same pattern as InstallInstructions.jsx. */
const COPY = {
  en: {
    nav: { features: "Features", pricing: "Pricing", academies: "Academies", student: "Student app", signin: "Sign in", langToggle: "Español" },
    ctaLabels: { nav: "Get RollOSS", hero: "Get Your Academy on RollOSS", bottom: "Bring RollOSS to your academy" },
    reqModal: {
      eyebrow: "Founding academies", title: "Request access", titleFull: "Get your academy on RollOSS", close: "Close",
      thanks: "Thanks — we'll reach out within 24h.",
      nameLabel: "Your full name", namePlaceholder: "Jane Doe",
      academyLabel: "Academy name", academyPlaceholder: "Your academy",
      emailLabel: "Owner email", emailPlaceholder: "owner@academy.com",
      locationLabel: "City / Country", locationPlaceholder: "Panama City, Panama",
      membersLabel: "Approx. active members", membersPlaceholder: "e.g. 80",
      submit: "Request access", submitFull: "Get started", footnote: "We're onboarding founding academies one-by-one.",
    },
    hero: {
      titleLine1: "The operating system", titleLine2: "for", titleAccent: "Jiu-Jitsu", titleLine3: " academies.",
      subtitle: "Schedules, attendance, belt progression, announcements. Everything your academy needs to run smoothly and keep mats full — set up in under 30 minutes.",
      viewPricing: "View pricing", note: "No credit card · 14-day trial · Cancel anytime",
    },
    preview: {
      sidebar: ["Dashboard", "Classes", "Attendance", "Students", "Analytics", "Announcements"],
      kpis: [{ l: "Active students", v: "184" }, { l: "Today's classes", v: "7" }, { l: "Check-ins · today", v: "96" }, { l: "Retention 30d", v: "94%" }],
      attendance: "Attendance · last 14 days",
    },
    features: {
      eyebrow: "Built for the mats",
      headingLine1: "Everything an academy needs.", headingLine2: "Nothing it doesn't.",
      items: [
        { icon: Calendar, title: "Class management", body: "Weekly schedule, instructor assignment, capacity limits — drag, drop, done." },
        { icon: ScanLine, title: "Attendance tracking", body: "QR check-in at the door. Live coach rosters. Auto-flag inactive students." },
        { icon: Users, title: "Student profiles", body: "Belt rank, stripes, membership status, training history — at a glance." },
        { icon: Award, title: "Belt progression", body: "Promotion history, stripe count, coach notes. Athletes see their path." },
        { icon: Megaphone, title: "Announcements", body: "Push academy updates, seminar invites, class changes to every member instantly." },
        { icon: Smartphone, title: "Mobile-first student app", body: "Reserve classes, track streaks, get reminders. Branded for your academy." },
      ],
    },
    flagship: {
      eyebrow: "Flagship academy", titleLine1: "Ludus Gladius", titleLine2: "runs on RollOSS.",
      body: "The first BJJ academy in our network — using RollOSS to manage classes, track promotions, and grow a culture of consistent training.",
      stats: [{ l: "Members", v: "184" }, { l: "Weekly classes", v: "32" }, { l: "Retention", v: "94%" }],
      established: "Jiu-Jitsu · Established 2018",
      classes: [{ t: "Fundamentals", s: "Today · 06:30", f: "22/24" }, { t: "Advanced Gi", s: "Today · 19:00", f: "18/20" }, { t: "Open Mat", s: "Today · 20:30", f: "14/30" }],
    },
    cta: {
      titleLine1: "Run your academy", titleLine2: "like a pro.",
      body: "Stop juggling spreadsheets and group chats. Give your students an experience worthy of the art.",
    },
    footer: { pricing: "Pricing", features: "Features", signin: "Sign in", student: "Student app", privacy: "Privacy", terms: "Terms", tag: "© RollOSS · Built for BJJ" },
  },
  es: {
    nav: { features: "Funciones", pricing: "Precios", academies: "Academias", student: "App de estudiante", signin: "Iniciar sesión", langToggle: "English" },
    ctaLabels: { nav: "Obtener RollOSS", hero: "Pon tu academia en RollOSS", bottom: "Lleva RollOSS a tu academia" },
    reqModal: {
      eyebrow: "Academias fundadoras", title: "Solicitar acceso", titleFull: "Pon tu academia en RollOSS", close: "Cerrar",
      thanks: "Gracias — te contactaremos en menos de 24h.",
      nameLabel: "Tu nombre completo", namePlaceholder: "Juana Pérez",
      academyLabel: "Nombre de la academia", academyPlaceholder: "Tu academia",
      emailLabel: "Correo del propietario", emailPlaceholder: "propietario@academia.com",
      locationLabel: "Ciudad / País", locationPlaceholder: "Ciudad de Panamá, Panamá",
      membersLabel: "Miembros activos aprox.", membersPlaceholder: "ej. 80",
      submit: "Solicitar acceso", submitFull: "Comenzar", footnote: "Estamos incorporando academias fundadoras una por una.",
    },
    hero: {
      titleLine1: "El sistema operativo", titleLine2: "para academias de", titleAccent: "Jiu-Jitsu", titleLine3: ".",
      subtitle: "Horarios, asistencia, progresión de cinturones, anuncios. Todo lo que tu academia necesita para funcionar sin fricción y mantener el tatame lleno — configúralo en menos de 30 minutos.",
      viewPricing: "Ver precios", note: "Sin tarjeta de crédito · 14 días de prueba · Cancela cuando quieras",
    },
    preview: {
      sidebar: ["Panel", "Clases", "Asistencia", "Estudiantes", "Analítica", "Anuncios"],
      kpis: [{ l: "Estudiantes activos", v: "184" }, { l: "Clases de hoy", v: "7" }, { l: "Check-ins · hoy", v: "96" }, { l: "Retención 30d", v: "94%" }],
      attendance: "Asistencia · últimos 14 días",
    },
    features: {
      eyebrow: "Hecho para el tatame",
      headingLine1: "Todo lo que una academia necesita.", headingLine2: "Nada de lo que no.",
      items: [
        { icon: Calendar, title: "Gestión de clases", body: "Horario semanal, asignación de instructores, límites de capacidad — arrastra, suelta, listo." },
        { icon: ScanLine, title: "Control de asistencia", body: "Check-in con QR en la puerta. Listas en vivo para el instructor. Detecta estudiantes inactivos al instante." },
        { icon: Users, title: "Perfiles de estudiantes", body: "Cinturón, grados, estado de membresía, historial de entrenamiento — de un vistazo." },
        { icon: Award, title: "Progresión de cinturón", body: "Historial de promociones, grados, notas del instructor. Los atletas ven su camino." },
        { icon: Megaphone, title: "Anuncios", body: "Envía novedades, invitaciones a seminarios y cambios de clase a todos los miembros al instante." },
        { icon: Smartphone, title: "App móvil para estudiantes", body: "Reserva clases, sigue tus rachas, recibe recordatorios. Con la marca de tu academia." },
      ],
    },
    flagship: {
      eyebrow: "Academia insignia", titleLine1: "Ludus Gladius", titleLine2: "funciona con RollOSS.",
      body: "La primera academia de BJJ en nuestra red — usa RollOSS para gestionar clases, seguir promociones y cultivar una cultura de entrenamiento constante.",
      stats: [{ l: "Miembros", v: "184" }, { l: "Clases semanales", v: "32" }, { l: "Retención", v: "94%" }],
      established: "Jiu-Jitsu · Fundada en 2018",
      classes: [{ t: "Fundamentos", s: "Hoy · 06:30", f: "22/24" }, { t: "Gi avanzado", s: "Hoy · 19:00", f: "18/20" }, { t: "Open Mat", s: "Hoy · 20:30", f: "14/30" }],
    },
    cta: {
      titleLine1: "Dirige tu academia", titleLine2: "como un profesional.",
      body: "Deja de hacer malabares con hojas de cálculo y chats grupales. Dale a tus estudiantes una experiencia digna del arte.",
    },
    footer: { pricing: "Precios", features: "Funciones", signin: "Iniciar sesión", student: "App de estudiante", privacy: "Privacidad", terms: "Términos", tag: "© RollOSS · Hecho para el BJJ" },
  },
};

const LangContext = createContext({ lang: "en", t: COPY.en });
const useT = () => useContext(LangContext);

function RequestAccessButton({ labelKey, testid = "cta-get-rolloss", variant = "primary", fields = "simple" }) {
  const { t } = useT();
  const label = t.ctaLabels[labelKey];
  const isFull = fields === "full";
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [academy, setAcademy] = useState("");
  const [location, setLocation] = useState("");
  const [members, setMembers] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const reset = () => { setOpen(false); setSent(false); setSubmitError(""); setName(""); setEmail(""); setAcademy(""); setLocation(""); setMembers(""); };
  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true); setSubmitError("");
    const body = { email, academy_name: academy, source: "landing" };
    if (isFull) {
      body.name = name;
      body.location = location;
      if (members) body.approx_members = Number(members);
    }
    try {
      await api.post("/leads", body);
      setSent(true);
    } catch (err) {
      console.error("Lead submission failed:", err);
      setSubmitError(err?.response?.data?.detail || "Something went wrong — please try again.");
    }
    setSubmitting(false);
  };
  return (
    <>
      <button onClick={()=>setOpen(true)} className={`btn btn-${variant}`} data-testid={testid}>{label} {variant==="primary" && <ArrowRight size={14}/>}</button>
      {open && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", display:"grid", placeItems:"center", zIndex:60 }} onClick={reset}>
          <div className="card p-7 w-[440px] max-w-[90vw]" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div><div className="label" style={{color:"var(--green)"}}>{t.reqModal.eyebrow}</div><div className="text-[20px] font-bold uppercase mt-1 tracking-tight">{isFull ? t.reqModal.titleFull : t.reqModal.title}</div></div>
              <button onClick={reset} data-testid="req-close" aria-label="Close"><X size={18}/></button>
            </div>
            {sent ? (
              <div className="py-6 text-center" data-testid="req-sent">
                <div style={{color:"var(--green)"}} className="flex items-center justify-center gap-2"><Check size={18}/> {t.reqModal.thanks}</div>
                <button onClick={reset} className="btn btn-outline mt-5">{t.reqModal.close}</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                {isFull && (
                  <label className="block label">{t.reqModal.nameLabel}<input required className="input mt-1" value={name} onChange={e=>setName(e.target.value)} placeholder={t.reqModal.namePlaceholder} data-testid="req-name"/></label>
                )}
                <label className="block label">{t.reqModal.academyLabel}<input required className="input mt-1" value={academy} onChange={e=>setAcademy(e.target.value)} placeholder={t.reqModal.academyPlaceholder} data-testid="req-academy"/></label>
                <label className="block label">{t.reqModal.emailLabel}<input required type="email" className="input mt-1" value={email} onChange={e=>setEmail(e.target.value)} placeholder={t.reqModal.emailPlaceholder} data-testid="req-email"/></label>
                {isFull && (
                  <>
                    <label className="block label">{t.reqModal.locationLabel}<input required className="input mt-1" value={location} onChange={e=>setLocation(e.target.value)} placeholder={t.reqModal.locationPlaceholder} data-testid="req-location"/></label>
                    <label className="block label">{t.reqModal.membersLabel}<input type="number" min="0" className="input mt-1" value={members} onChange={e=>setMembers(e.target.value)} placeholder={t.reqModal.membersPlaceholder} data-testid="req-members"/></label>
                  </>
                )}
                {submitError && <div className="label-tiny" style={{color:"var(--danger)"}} data-testid="req-error">{submitError}</div>}
                <button type="submit" disabled={submitting} className="btn btn-primary w-full mt-2" data-testid="req-submit">
                  {submitting ? "…" : (isFull ? t.reqModal.submitFull : t.reqModal.submit)} <ArrowRight size={14}/>
                </button>
                <div className="label-tiny text-center mt-3">{t.reqModal.footnote}</div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Nav() {
  const { t, lang, setLang } = useT();
  return (
    <header className="px-6 md:px-8 py-5 flex items-center justify-between border-b" style={{ borderColor: "var(--border)" }}>
      <Link to="/" className="flex items-center" data-testid="logo-link"><Wordmark /></Link>
      <nav className="hidden md:flex items-center gap-8 text-sm" style={{ color: "var(--text-dim)" }}>
        <a href="#features" data-testid="nav-features" className="hover:text-white transition-colors">{t.nav.features}</a>
        <Link to="/pricing" data-testid="nav-pricing" className="hover:text-white transition-colors">{t.nav.pricing}</Link>
        <a href="#academies" data-testid="nav-academies" className="hover:text-white transition-colors">{t.nav.academies}</a>
        <Link to="/student/login" data-testid="nav-student" className="hover:text-white transition-colors">{t.nav.student}</Link>
        <Link to="/app/login" data-testid="nav-owner-signin" className="hover:text-white transition-colors">{t.nav.signin}</Link>
      </nav>
      <div className="flex items-center gap-3 md:gap-4">
        <button onClick={() => setLang(lang === "en" ? "es" : "en")} className="text-sm underline hover:text-white transition-colors" style={{ color: "var(--text-dim)" }} data-testid="landing-lang-toggle">
          {t.nav.langToggle}
        </button>
        <RequestAccessButton labelKey="nav" testid="nav-cta"/>
      </div>
    </header>
  );
}

function Hero() {
  const { t } = useT();
  return (
    <section className="grid-bg px-6 md:px-8 pt-16 md:pt-24 pb-16 text-center">
      <h1 className="hero-title mx-auto max-w-[1100px]">
        {t.hero.titleLine1}<br />{t.hero.titleLine2} <span className="hero-accent">{t.hero.titleAccent}</span>{t.hero.titleLine3}
      </h1>
      <p className="mt-7 mx-auto max-w-[640px] text-[16px] md:text-[17px] leading-relaxed" style={{ color: "var(--text-dim)" }}>
        {t.hero.subtitle}
      </p>
      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <RequestAccessButton labelKey="hero" testid="hero-start-trial" fields="full"/>
        <Link to="/pricing" className="btn btn-outline" data-testid="hero-view-pricing">{t.hero.viewPricing}</Link>
      </div>
      <div className="label mt-5">{t.hero.note}</div>
    </section>
  );
}

function DashboardPreview() {
  const { t } = useT();
  return (
    <div className="card mx-4 md:mx-8 -mt-4 overflow-hidden hidden md:block" style={{ boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6)" }}>
      <div className="flex items-center gap-3 px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="flex gap-1.5">
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#3a3f3c" }} />
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#3a3f3c" }} />
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#3a3f3c" }} />
        </div>
        <div className="label-tiny ml-2">app.getrolloss.app / dashboard</div>
      </div>
      <div className="grid grid-cols-12 min-h-[420px]">
        <aside className="col-span-2 border-r p-5 space-y-1" style={{ borderColor: "var(--border)" }}>
          {t.preview.sidebar.map((x,i) => (
            <div key={x} className={`sidebar-link text-[13px] ${i===0?"active":""}`}>
              <span style={{ width:6,height:6,borderRadius:999, background:i===0?"var(--green)":"var(--text-muted)"}} />
              {x}
            </div>
          ))}
        </aside>
        <div className="col-span-10 p-6">
          <div className="grid grid-cols-4 gap-4">
            {t.preview.kpis.map(k=>(
              <div key={k.l} className="card kpi-card">
                <div className="label">{k.l}</div>
                <div className="kpi-value mt-3">{k.v}</div>
              </div>
            ))}
          </div>
          <div className="card mt-4 p-6">
            <div className="label mb-4">{t.preview.attendance}</div>
            <div className="flex items-end gap-2 h-32">
              {[40,55,48,62,58,70,65,72,68,80,75,84,82,96].map((h,i,arr)=>(
                <div key={i} className="bar flex-1 rounded-t-md" style={{ height:`${h}%`, background: i===arr.length-1?"var(--green)":"var(--border-strong)"}}/>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Features() {
  const { t } = useT();
  return (
    <section id="features" className="px-6 md:px-8 py-24 md:py-28">
      <div className="label text-center">{t.features.eyebrow}</div>
      <h2 className="text-center mt-3 text-[34px] md:text-[56px] font-bold tracking-tight leading-[1.05] max-w-[860px] mx-auto">
        {t.features.headingLine1}<br /><span style={{ color: "var(--text-dim)" }}>{t.features.headingLine2}</span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-14 max-w-[1200px] mx-auto">
        {t.features.items.map((f, i) => (
          <div key={f.title} className="card card-hover p-7" data-testid={`feature-${i}`}>
            <f.icon size={22} style={{ color: "var(--green)" }} />
            <h3 className="mt-4 text-[18px] font-semibold tracking-tight">{f.title}</h3>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "var(--text-dim)" }}>{f.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Flagship() {
  const { t } = useT();
  return (
    <section id="academies" className="px-6 md:px-8 py-20 md:py-24">
      <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="label">{t.flagship.eyebrow}</div>
          <h2 className="text-[34px] md:text-[52px] font-bold tracking-tight leading-[1.05] mt-3">
            {t.flagship.titleLine1}<br />{t.flagship.titleLine2}
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed max-w-[480px]" style={{ color: "var(--text-dim)" }}>
            {t.flagship.body}
          </p>
          <div className="grid grid-cols-3 gap-5 mt-8 max-w-[460px]">
            {t.flagship.stats.map(s=>(
              <div key={s.l}>
                <div className="label-tiny">{s.l}</div>
                <div className="text-[28px] font-bold mt-1">{s.v}</div>
              </div>
            ))}
          </div>
          <a href="https://www.instagram.com/ludus.gladius/" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 mt-7 text-sm" style={{ color: "var(--green)" }}>
            @ludus.gladius <ArrowRight size={14} />
          </a>
        </div>
        <div className="card p-7">
          <div className="flex items-center gap-3">
            <div className="logo-mark" style={{ background: "#1a1a1a", color: "var(--green)", border: "1px solid var(--border)" }}>LG</div>
            <div>
              <div className="font-semibold">Ludus Gladius</div>
              <div className="label-tiny mt-1">{t.flagship.established}</div>
            </div>
          </div>
          <div className="divider my-5" />
          {t.flagship.classes.map(c=>(
            <div key={c.t} className="flex items-center justify-between py-3">
              <div>
                <div className="font-semibold text-[15px]">{c.t}</div>
                <div className="label-tiny mt-0.5">{c.s}</div>
              </div>
              <div className="mono text-sm" style={{ color: "var(--green)" }}>{c.f}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const { t } = useT();
  return (
    <section className="px-6 md:px-8 py-20 md:py-24 text-center">
      <h2 className="text-[34px] md:text-[56px] font-bold tracking-tight leading-[1.05] max-w-[800px] mx-auto">
        {t.cta.titleLine1}<br />{t.cta.titleLine2}
      </h2>
      <p className="mt-5 text-[16px] max-w-[560px] mx-auto" style={{ color: "var(--text-dim)" }}>
        {t.cta.body}
      </p>
      <div className="mt-10 flex justify-center">
        <RequestAccessButton labelKey="bottom" testid="cta-bottom"/>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useT();
  return (
    <footer className="px-6 md:px-8 py-14 border-t flex flex-col items-center gap-7" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
      <LogoLockup size={30} beltWidth={260} />
      <div className="flex flex-wrap items-center justify-center gap-6 label-tiny mt-2">
        <Link to="/pricing" className="hover:text-white" data-testid="footer-pricing">{t.footer.pricing}</Link>
        <a href="#features" className="hover:text-white">{t.footer.features}</a>
        <Link to="/app/login" className="hover:text-white" data-testid="footer-signin">{t.footer.signin}</Link>
        <Link to="/student/login" className="hover:text-white" data-testid="footer-student">{t.footer.student}</Link>
        <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">{t.footer.privacy}</Link>
        <Link to="/terms" className="hover:text-white" data-testid="footer-terms">{t.footer.terms}</Link>
        <span>{t.footer.tag}</span>
      </div>
    </footer>
  );
}

export default function Landing() {
  const { user, loading } = useAuth();
  const [lang, setLang] = useState("en");

  // If a signed-in user opens the landing (e.g. via PWA `start_url=/`), send
  // them straight to the right shell based on role. Students → student app,
  // Owners/Coaches → owner app. Only kicks in AFTER auth check completes so
  // we never bounce an anonymous marketing-page visitor.
  if (!loading && user) {
    if (user.role === "student") return <Navigate to="/student" replace />;
    if (user.role === "super_admin") return <Navigate to="/admin" replace />;
    if (user.role === "owner" || user.role === "coach") return <Navigate to="/app" replace />;
  }

  return (
    <LangContext.Provider value={{ lang, setLang, t: COPY[lang] }}>
      <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
        <Nav />
        <Hero />
        <DashboardPreview />
        <Features />
        <Flagship />
        <CTA />
        <Footer />
      </div>
    </LangContext.Provider>
  );
}
